import { action, computed, observable, makeAutoObservable } from 'mobx';
import {
    PromotedEventsState,
    CompetitionCombinedType,
} from 'src/AppState/CmsState/PromotedEventsState/PromotedEventsState';
import { TrpcClient } from 'src/AppState/TrpcClient';

export class PromotedEventCompetitionState {
    @observable isOpenSection: boolean = false;
    @observable isActive: boolean = false;
    @observable showDeletePopup: boolean = false;

    constructor(
        private readonly trpcClient: TrpcClient,
        private readonly competitionId: number,
        private readonly getCompetitionData: (competitionId: number) => CompetitionCombinedType | null,
        private readonly handleDeleteSport: () => Promise<void>,
        private readonly promotedEventsSectionState: PromotedEventsState
    ) {
        this.isActive = this.getCompetitionData(this.competitionId)?.is_active ?? false;
        makeAutoObservable(this);
    }

    @computed get competitionData(): CompetitionCombinedType | null {
        return this.getCompetitionData(this.competitionId) ?? null;
    }

    @action public setCompetitionActive = async (): Promise<void> => {
        if (this.competitionData !== null) {
            const promotedCompetitionDetailsData = {
                ...this.competitionData,
                is_active: !this.competitionData.is_active,
            };

            this.isActive = !this.isActive;

            try {
                await this.trpcClient.client.promotedCompetitions.updatePromotedCompetition.mutate({
                    data: promotedCompetitionDetailsData,
                    id: this.competitionData.id,
                });

                await this.promotedEventsSectionState.promotedEventsResource.refresh();
            } catch (error) {
                console.error(error);
            }
        } else {
            console.error('Cannot find the competition with provided ID.');
        }
    };

    @action public handleToggleOpenSection = (): void => {
        this.isOpenSection = !this.isOpenSection;
    };

    @action handleHideDeletePopup = (): void => {
        this.showDeletePopup = false;
    };

    @action handleShowDeletePopup = (): void => {
        this.showDeletePopup = true;
    };

    @action public handleDeleteCompetition = async (): Promise<void> => {
        if (this.competitionData !== null) {
            const sportSlug = this.competitionData.sport_id;
            const removeResult = await this.trpcClient.client.promotedCompetitions.deletePromotedCompetition.mutate({
                id: this.competitionData.id,
            });

            if (removeResult.status === 200) {
                const eventIds = this.competitionData.events_list.map((e) => e.id);
                await this.handleDeleteAllEvents(eventIds);
                await this.promotedEventsSectionState.promotedEventsResource.refresh();
                this.promotedEventsSectionState.setSportsDataFromResource();

                const competitionsList = this.promotedEventsSectionState.getSportData(sportSlug)?.competitions_list;
                if (competitionsList?.length === 0) {
                    await this.handleDeleteSport();
                }
            } else {
                console.error("The item couldn't be deleted.");
            }
        }
    };

    @action public handleDeleteAllEvents = async (eventIds: Array<number>): Promise<void> => {
        for (const id of eventIds) {
            const removeResult = await this.trpcClient.client.promotedEvents.deletePromotedEvent.mutate({
                id,
            });

            if (removeResult.status === 200) {
                await this.promotedEventsSectionState.promotedEventsResource.refresh();
            } else {
                console.error("The item couldn't be deleted.");
            }
        }
    };
}
